import { Injectable, inject } from '@angular/core';
import { MapGeocoder } from '@angular/google-maps';

import { Accessory, Phone, Product, QuoteModel } from '@bytel/bytel-sales';
import {
    CartCheckoutService,
    CheckoutCartModel,
    Coordinates,
    DeliveryService,
    GcpService,
    IAddress,
    IStorageAddress,
    SHIPPING_MODES,
    ShippingMethodModel,
    UserRefCliData,
    UserService,
} from '@bytel/checkout';

import { GcpRequest } from '@base/common/models/gcp.interface';

import { MainCartService } from '@services/main-cart.service';

import { AddressRepository } from '@repositories/address.repository';

import { BehaviorSubject, Observable, ReplaySubject, concat, forkJoin, mergeMap, of } from 'rxjs';
import { catchError, last, map, tap } from 'rxjs/operators';

import { CLIENT_TYPE } from '../common/constants';
import { IFaiAddressInstallation } from '../interfaces/fai.interface';

@Injectable({
    providedIn: 'root',
})
export class AddressService {
    public billingAddressFromStorage: boolean = false;
    public shippingAddress$: ReplaySubject<IAddress> = new ReplaySubject<IAddress>(1);
    public billingAddress$: ReplaySubject<IAddress> = new ReplaySubject<IAddress>(1);
    public addressModification$: ReplaySubject<IAddress> = new ReplaySubject<IAddress>(1);
    public selectedShippingMethod: ShippingMethodModel = null;
    public errorShippingAddress$: BehaviorSubject<string> = new BehaviorSubject(null);
    public errorBillingProAddress$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private readonly _shippingPickup = [
        SHIPPING_MODES.CLICK_AND_COLLECT,
        SHIPPING_MODES.RELAIS_COLIS,
        SHIPPING_MODES.RELAIS_EXPRESS,
    ];

    private geocoder: MapGeocoder = inject(MapGeocoder);
    private cartService: MainCartService = inject(MainCartService);
    private gcpService: GcpService = inject(GcpService);
    private userService: UserService = inject(UserService);
    private addressRepo: AddressRepository = inject(AddressRepository);
    private deliveryService: DeliveryService = inject(DeliveryService);
    private checkoutCartService: CartCheckoutService = inject(CartCheckoutService);

    public getShippingMethods(
        cart = this.cartService.cartModel,
        quoteIndex: number = this.cartService.cartModel.currentQuoteIndex,
    ): Observable<ShippingMethodModel[]> {
        return this.addressRepo.getAvailableShippingMethods(cart, quoteIndex).pipe(
            map((methods) => methods.sort((a, b) => a.price - b.price)),
            catchError((err) => {
                this.errorShippingAddress$.next(err?.error?.error_description);
                return of([]);
            }),
        );
    }

    public updateShippingMethods(cartModel: CheckoutCartModel = this.cartService.cartModel): Observable<boolean> {
        const obs: Observable<ShippingMethodModel>[] = [];
        cartModel.quotes.forEach((quote, index) => {
            const quoteDelivery = this.deliveryService.getDelivery(this.checkoutCartService, cartModel, index);
            if (this._shouldSkipUpdateShippingMethods(index, cartModel)) {
                return;
            } else {
                obs.push(
                    this.addressRepo.postShippingMethods(quoteDelivery, cartModel, quote, this.getShipping(index)),
                );
            }
        });

        return concat(...obs).pipe(
            last(),
            map(() => true),
        );
    }

    public updateBillingAddress(address: IAddress): Observable<boolean> {
        const obs: Observable<{ message: string }>[] = [];
        this.cartService.cartModel.quotes.forEach((quote) => {
            obs.push(this.addressRepo.postBillingAddress(address, this.cartService.cartModel, quote.id));
        });

        return concat(...obs).pipe(
            last(),
            map(() => true),
        );
    }

    public saveBilling(billing: IAddress): Observable<boolean> {
        return this.updateBillingAddress(billing).pipe(
            catchError((error) => {
                // Handle 400 error for billing pro address
                if (this.cartService.cartModel?.isPro && error?.status === 400) {
                    this.errorBillingProAddress$.next(true);
                    return of(false);
                }
                return of(true);
            }),
            mergeMap((data) => {
                if (data) {
                    const addressFromStorage: IAddress = this.userService.getAddress();
                    const modifierprospect: string = `/personnes/${this.userService.user.id}`;
                    return !addressFromStorage && !this.cartService.cartModel.isPro
                        ? this.userService.editProspect(modifierprospect, this._getUserRefCliData(billing)).pipe(
                              catchError((error) => {
                                  console.error(error);
                                  return of(true);
                              }),
                          )
                        : of(true);
                } else {
                    return of(false);
                }
            }),
            tap((data) => {
                if (data) {
                    this.cartService.cartModel.quotes.forEach((quote) => {
                        quote.address = { ...quote.address, billing };
                    });
                    this.cartService.save();
                    this.billingAddress$.next(billing);
                }
            }),
            map(() => true),
        );
    }

    public getBilling(quoteIndex: number = 0): IAddress {
        const address: IStorageAddress = this._getStorageAddress(quoteIndex);
        return address ? address.billing : null;
    }

    public saveShipping(shipping: IAddress): void {
        const currentQuote: QuoteModel = this.cartService.getQuote();
        const isIdentical: boolean = currentQuote?.address?.shipping === shipping;
        if (!isIdentical) {
            this.cartService.cartModel.quotes.forEach((quote) => {
                quote.address = { ...quote.address, shipping };
            });
            this.cartService.save();
            this.shippingAddress$.next(shipping);
        }
    }

    public getShipping(quoteIndex: number = 0): IAddress {
        const address: IStorageAddress = this._getStorageAddress(quoteIndex);
        return address ? address.shipping : null;
    }

    public clearShipping(isSameAddress: boolean, quoteIndex: number = 0): void {
        const address: IStorageAddress = this._getStorageAddress(quoteIndex);
        if (address) {
            const shippingAddress = isSameAddress ? address.billing : null;
            this.saveShipping(shippingAddress);
        }
    }

    public clearBilling(): void {
        const currentQuote: QuoteModel = this.cartService.getQuote();
        currentQuote.address.billing = null;
        this.cartService.save();
        this.billingAddress$.next(null);
    }

    public clearDeliveryInfos(): void {
        if (this.cartService.cartModel.getQuote().forms?.deliveryInfo) {
            delete this.cartService.cartModel.getQuote().forms.deliveryInfo;
        }
    }

    public removeDelivery(delivery: Product): Observable<Product[]> {
        const obs = [];
        this.cartService.cartModel.quotes.forEach((quote, quoteIndex) => {
            obs.push(this.cartService.removeProduct(delivery, quoteIndex));
        });

        return forkJoin(obs).pipe(tap(() => this.clearDeliveryInfos()));
    }

    public getInlineAddress(address: IAddress): string {
        if (!address || !address.codePostal) {
            return '';
        }

        return `${this._getSafeValue(address.numeroRue)} ${address.rue}, ${this._getSafeValue(
            address.complementAdresse,
        )} ${address.codePostal} ${address.ville}`;
    }

    public getInlineGcpAddress(formValue: any): string {
        return `${this._getSafeValue(formValue.numero)} ${formValue.voie}, ${formValue.codePostal} ${formValue.ville}`;
    }

    public getCoordinatesFromGoogle(address: IAddress): Observable<Coordinates> {
        const inlineAddress: string = `${address.numeroRue}+${address.rue},${address.ville}`;
        return this.geocoder.geocode({ address: inlineAddress }).pipe(
            map((data) => {
                if (data.status === 'OK') {
                    return data.results[0].geometry?.location.toJSON();
                }

                return null;
            }),
            catchError(() => of(null)),
        );
    }

    public isSameAddress(shipping: IAddress, billing: IAddress): boolean {
        if (shipping && billing) {
            return this.getInlineAddress(shipping) === this.getInlineAddress(billing);
        }
        return true;
    }

    public mapFromGcpToAddress(data: GcpRequest, complement: string): IAddress {
        return {
            codePostal: data.codePostal,
            ville: data.ville,
            rue: data.voie,
            pays: data.pays,
            // GCP return empty string when address has no number
            numeroRue: data.numero?.length > 0 ? data.numero : null,
            complementAdresse: complement,
            coordonnees: data.coordonnees,
        };
    }

    public mapFromAddressToGcp(address: IAddress): GcpRequest {
        return {
            numero: address.numeroRue,
            voie: address.rue,
            complement: address.complementAdresse,
            codePostal: address.codePostal,
            ville: address.ville,
            pays: address.pays,
            origineST: this.gcpService.ORIGINEST,
            coordonnees: address.coordonnees,
        };
    }

    public mapFromEligibilityAddressToAddress(address: IFaiAddressInstallation): IAddress {
        return {
            codePostal: address?.codePostal,
            ville: address?.commune,
            rue: address?.voie,
            numeroRue: address?.numeroVoie,
            complementAdresse: address?.complementVoie,
        };
    }

    private _getSafeValue(value: string): string {
        return value != null && typeof value !== 'undefined' ? value : '';
    }

    private _getStorageAddress(quoteIndex: number): IStorageAddress {
        const currentQuote: QuoteModel = this.cartService.getQuote(quoteIndex);
        return currentQuote.address;
    }

    private _getUserRefCliData(billing: IAddress): Partial<UserRefCliData> {
        return {
            noPersonne: this.userService.user.id,
            type: this.cartService.cartModel.isPro ? CLIENT_TYPE.PRO : CLIENT_TYPE.INDIVIDU,
            adresseDeclaree: {
                codePays: 'FRA',
                codePostal: billing.codePostal,
                numero: billing.numeroRue,
                rue: billing.rue,
                ville: billing.ville,
                complement: billing.complementAdresse,
            },
        };
    }

    private _shouldSkipUpdateShippingMethods(
        index: number,
        cartModel: CheckoutCartModel = this.cartService.cartModel,
    ): boolean {
        const quoteDelivery = this.deliveryService.getDelivery(this.checkoutCartService, cartModel, index);
        return (
            (quoteDelivery &&
                this._shippingPickup.includes(quoteDelivery.gencode as SHIPPING_MODES) &&
                !quoteDelivery.pickup) ||
            (this.cartService.quoteHasEsim(index) &&
                !cartModel.getQuote().getProductByType(Phone) &&
                !cartModel.getQuote().getProductByType(Accessory))
        );
    }
}
